import { useRef, useEffect, useState } from "react";
import {
  ChevronRight,
  ChevronDown,
  Check,
  PillBottle,
  Settings2,
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Share2,
} from "lucide-react";
import useDark from "../../Center/Graph/hooks/useDark";
import { useCompanyContext } from "../../../context";

const cyan = "14,165,233";
const MAX_ACTIVE = 10;
const MAX_INACTIVE = 90;
const PAGE_SIZE = 10;

const CompanySelect = () => {
  const dark = useDark();
  const [open, setOpen] = useState(false);
  const [managing, setManaging] = useState(false);
  const [inactiveExpanded, setInactiveExpanded] = useState(false);
  const [visibleInactive, setVisibleInactive] = useState(PAGE_SIZE);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmPublic, setConfirmPublic] = useState(null);
  const ref = useRef(null);

  const {
    companies = [],
    currentCompany,
    selectCompany,
    toggleCompany,
    removeCompany,
    refreshCompanies,
    setShowAddCompany,
    showAddCompany,
  } = useCompanyContext();

  const activeCases = companies.filter((c) => c.active);
  const inactiveCases = companies.filter((c) => !c.active);
  const activeAtMax = activeCases.length >= MAX_ACTIVE;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (showAddCompany) return;
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setManaging(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, showAddCompany]);

  // Reset state when dropdown closes
  useEffect(() => {
    if (!open) {
      setManaging(false);
      setConfirmDelete(null);
      setConfirmPublic(null);
      setInactiveExpanded(false);
      setVisibleInactive(PAGE_SIZE);
    }
  }, [open]);

  // When PopUpSearch closes, snap back to select mode
  const prevShowAddRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevShowAddRef.current;
    prevShowAddRef.current = showAddCompany;
    if (wasOpen && !showAddCompany) {
      setManaging(false);
    }
  }, [showAddCompany]);

  // Load companies when entering manage mode
  useEffect(() => {
    if (managing) refreshCompanies();
  }, [managing]);

  const getDisplayName = (company) => {
    if (!company?.case) return "Select Rx brand name";
    try {
      return new URL(company.case).hostname.replace(/^www\./, "");
    } catch {
      return company.case;
    }
  };

  const handleToggle = async (company) => {
    const willActivate = !company.active;
    if (willActivate && activeAtMax) return;
    setTogglingId(company.case);
    try {
      await toggleCompany(company, willActivate);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (company) => {
    setDeletingId(company.case);
    try {
      await removeCompany(company);
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  const handleMakePublic = async (company) => {
    setPublishingId(company.case);
    try {
      // TODO: wire up backend once available
      // await setCompanyVisibility(company.case, !company.public);
      // await refreshCompanies();
    } finally {
      setPublishingId(null);
      setConfirmPublic(null);
    }
  };

  const activeBorder = dark ? `rgba(${cyan},0.38)` : `rgba(100,116,139,0.28)`;
  const activeGlow = dark
    ? `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(${cyan},0.06) 0%, transparent 100%)`
    : "none";

  // ─── Manage row ───────────────────────────────────────────────────────────
  const ManageRow = ({ company }) => {
    const name = company.case;
    const display = getDisplayName({ case: name });
    const isActive = company.active;
    const isPublic = !!company.public; // adjust field name once backend confirmed
    const isToggling = togglingId === name;
    const isDeleting = deletingId === name;
    const isPublishing = publishingId === name;
    const busy = isToggling || isDeleting || isPublishing;
    const confirmingDel = confirmDelete === name;
    const confirmingPub = confirmPublic === name;

    return (
      <div className="group/mrow">
        <div
          className={[
            "flex items-center gap-x-2.5 px-2.5 py-[7px] rounded-lg transition-colors duration-150",
            busy ? "opacity-50" : "hover:bg-mainButtonBg/50",
          ].join(" ")}
        >
          <PillBottle
            strokeWidth={1.75}
            className="h-[15px] w-[15px] shrink-0 transition-colors duration-150"
            style={{ color: isActive ? `rgb(${cyan})` : undefined }}
          />
          <span
            className={[
              "flex-1 min-w-0 truncate text-[13px] font-normal tracking-wide transition-colors duration-150",
              isActive ? "text-velBlue" : "text-mutedText",
            ].join(" ")}
            title={name}
          >
            {display}
          </span>

          <div className="flex items-center gap-x-2 shrink-0">
            {/* Share / make public — hover only */}
            <button
              type="button"
              disabled={busy}
              title={isPublic ? "Make private" : "Make public"}
              onClick={() => {
                setConfirmDelete(null);
                setConfirmPublic(confirmingPub ? null : name);
              }}
              className="opacity-0 group-hover/mrow:opacity-100 p-1 rounded-md hover:bg-sky-500/10 transition-all duration-150 outline-none disabled:cursor-not-allowed"
            >
              {isPublishing ? (
                <Loader2
                  className="h-3.5 w-3.5 animate-spin"
                  style={{ color: `rgb(${cyan})` }}
                  strokeWidth={2}
                />
              ) : (
                <Share2
                  className="h-3.5 w-3.5 text-mutedText hover:text-velBlue transition-colors"
                  strokeWidth={1.75}
                  style={isPublic ? { color: `rgb(${cyan})` } : undefined}
                />
              )}
            </button>

            {/* Trash — hover only */}
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirmPublic(null);
                setConfirmDelete(confirmingDel ? null : name);
              }}
              className="opacity-0 group-hover/mrow:opacity-100 p-1 rounded-md hover:bg-red-500/10 transition-all duration-150 outline-none disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <Loader2
                  className="h-3.5 w-3.5 text-red-400 animate-spin"
                  strokeWidth={2}
                />
              ) : (
                <Trash2
                  className="h-3.5 w-3.5 text-mutedText hover:text-red-400 transition-colors"
                  strokeWidth={1.75}
                />
              )}
            </button>

            {/* Toggle */}
            <button
              type="button"
              disabled={busy || (!isActive && activeAtMax)}
              onClick={() => handleToggle(company)}
              title={
                isActive
                  ? "Deactivate"
                  : activeAtMax
                    ? `Max ${MAX_ACTIVE} active`
                    : "Activate"
              }
              className="relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 outline-none disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                background: isActive ? `rgb(${cyan})` : undefined,
                boxShadow: isActive ? `0 0 8px rgba(${cyan},0.35)` : undefined,
              }}
            >
              <span
                className={[
                  "absolute inset-0 rounded-full transition-colors duration-200",
                  isActive ? "" : "bg-mainButtonBg",
                ].join(" ")}
              />
              {isToggling ? (
                <Loader2
                  className="absolute inset-0 m-auto h-3 w-3 text-white animate-spin z-10"
                  strokeWidth={2.5}
                />
              ) : (
                <span
                  className={[
                    "absolute top-[3px] h-[14px] w-[14px] rounded-full bg-white shadow-sm z-10 transition-all duration-200",
                    isActive ? "left-[19px]" : "left-[3px]",
                  ].join(" ")}
                />
              )}
            </button>
          </div>
        </div>

        {/* Inline make-public confirm */}
        <div
          className={[
            "overflow-hidden transition-all duration-200 ease-in-out",
            confirmingPub ? "max-h-[40px] opacity-100" : "max-h-0 opacity-0",
          ].join(" ")}
        >
          <div className="flex items-center gap-x-2 px-3 py-1.5">
            <span className="text-[11px] text-mutedText">
              {isPublic ? "Make private?" : "Make public?"}
            </span>
            <button
              onClick={() => setConfirmPublic(null)}
              className="text-[11px] px-2 py-0.5 rounded border border-mainDivider text-mutedText hover:bg-mainButtonBg/50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleMakePublic(company)}
              className="text-[11px] px-2 py-0.5 rounded border transition-colors"
              style={{
                borderColor: `rgba(${cyan},0.3)`,
                color: `rgb(${cyan})`,
                background: `rgba(${cyan},0.05)`,
              }}
            >
              Yes
            </button>
          </div>
        </div>

        {/* Inline delete confirm */}
        <div
          className={[
            "overflow-hidden transition-all duration-200 ease-in-out",
            confirmingDel ? "max-h-[40px] opacity-100" : "max-h-0 opacity-0",
          ].join(" ")}
        >
          <div className="flex items-center gap-x-2 px-3 py-1.5">
            <span className="text-[11px] text-mutedText">Delete?</span>
            <button
              onClick={() => setConfirmDelete(null)}
              className="text-[11px] px-2 py-0.5 rounded border border-mainDivider text-mutedText hover:bg-mainButtonBg/50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(company)}
              className="text-[11px] px-2 py-0.5 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes arwes-nav-pulse {
          0%,100% { box-shadow: 0 0 0 1px rgba(${cyan},0.06), 0 0 12px 2px rgba(${cyan},0.08), 0 0 28px 3px rgba(${cyan},0.03); }
          50%      { box-shadow: 0 0 0 1px rgba(${cyan},0.12), 0 0 16px 3px rgba(${cyan},0.13), 0 0 40px 5px rgba(${cyan},0.05); }
        }
      `}</style>

      <div ref={ref} className="relative px-2.5">
        {/* ── Trigger ── */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={[
            "group w-full flex items-center rounded-lg px-2.5 py-1.5",
            "transition-all duration-200 ease-out outline-none",
            "focus-visible:ring-2 focus-visible:ring-inputFocus/60",
            open ? "bg-mainButtonBg/50" : "hover:bg-mainButtonBg/50",
          ].join(" ")}
          style={
            open
              ? {
                  border: `1px solid ${activeBorder}`,
                  background: activeGlow !== "none" ? activeGlow : undefined,
                  animation: "arwes-nav-pulse 5s ease-in-out infinite",
                }
              : { border: "1px solid transparent" }
          }
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <div className="relative z-10 flex items-center justify-between gap-x-2.5 min-h-[28px] w-full">
            <div className="flex items-center gap-x-2.5 min-w-0">
              <PillBottle
                strokeWidth={1.75}
                className={[
                  "h-[18px] w-[18px] shrink-0 transition-colors duration-200",
                  open
                    ? "text-velBlue"
                    : "text-mutedText group-hover:text-mainText",
                ].join(" ")}
              />
              <span
                className={[
                  "truncate text-[13px] font-normal tracking-wide transition-colors duration-200 text-left min-w-0",
                  open
                    ? "text-velBlue"
                    : "text-mutedText group-hover:text-mainText",
                ].join(" ")}
              >
                {getDisplayName(currentCompany)}
              </span>
            </div>
            <ChevronRight
              strokeWidth={2}
              className={[
                "h-3.5 w-3.5 shrink-0 transition-all duration-200",
                open
                  ? "text-velBlue"
                  : "text-mutedText group-hover:text-mainText",
              ].join(" ")}
            />
          </div>
        </button>

        {/* ── Right-flying popover ── */}
        <div
          role="listbox"
          className={[
            "fixed z-[200] rounded-lg overflow-hidden",
            "border border-mainDivider bg-mainBg",
            "transition-all duration-200 ease-out origin-left",
            open
              ? "opacity-100 scale-x-100 pointer-events-auto"
              : "opacity-0 scale-x-95 pointer-events-none",
          ].join(" ")}
          style={{
            top: ref.current ? ref.current.getBoundingClientRect().top : 0,
            left: ref.current
              ? ref.current.getBoundingClientRect().right + 8
              : 0,
            width: 280,
            maxHeight: "calc(100vh - 16px)",
            overflowY: "auto",
            ...(open && dark
              ? {
                  boxShadow: `0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(${cyan},0.08), 0 0 20px rgba(${cyan},0.04)`,
                }
              : {}),
          }}
        >
          {!managing ? (
            /* ══ SELECT MODE ══ */
            <>
              <div className="py-1.5 flex flex-col gap-y-0.5 px-1.5">
                {activeCases.map((company, index) => {
                  const isSelected = currentCompany?.case === company.case;
                  return (
                    <div
                      key={company.case ?? index}
                      className="rounded-lg overflow-hidden transition-all duration-200 ease-out"
                      style={
                        isSelected
                          ? {
                              border: `1px solid ${activeBorder}`,
                              background:
                                activeGlow !== "none" ? activeGlow : undefined,
                              animation:
                                "arwes-nav-pulse 5s ease-in-out infinite",
                            }
                          : { border: "1px solid transparent" }
                      }
                    >
                      <button
                        role="option"
                        aria-selected={isSelected}
                        type="button"
                        onClick={() => {
                          selectCompany(company);
                          setOpen(false);
                        }}
                        className={[
                          "group w-full flex items-center rounded-lg px-2.5 py-1.5",
                          "transition-colors duration-150 outline-none text-left",
                          "focus-visible:ring-2 focus-visible:ring-inputFocus/60",
                          !isSelected && "hover:bg-mainButtonBg/50",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <div className="flex items-center justify-between gap-x-2.5 min-h-[28px] w-full">
                          <div className="flex items-center gap-x-2.5 min-w-0">
                            <PillBottle
                              strokeWidth={1.75}
                              className={[
                                "h-[18px] w-[18px] shrink-0 transition-colors duration-150",
                                isSelected
                                  ? "text-velBlue"
                                  : "text-mutedText group-hover:text-mainText",
                              ].join(" ")}
                            />
                            <span
                              className={[
                                "flex-1 min-w-0 truncate text-[13px] font-normal tracking-wide transition-colors duration-150",
                                isSelected
                                  ? "text-velBlue"
                                  : "text-mutedText group-hover:text-mainText",
                              ].join(" ")}
                            >
                              {getDisplayName(company)}
                            </span>
                          </div>
                          {isSelected && (
                            <Check
                              className="h-3.5 w-3.5 shrink-0 text-velBlue"
                              strokeWidth={2.5}
                            />
                          )}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>

              {(!companies || companies.length === 0) && (
                <p className="text-[13px] font-normal tracking-wide text-mutedText/50 p-4 pt-1">
                  No Rx brand names added.
                </p>
              )}

              <div className="border-t border-mainDivider mx-1.5" />
              <div className="px-1.5 py-1.5">
                <button
                  type="button"
                  onClick={() => setManaging(true)}
                  className="group w-full flex items-center rounded-lg px-2.5 py-1.5 hover:bg-mainButtonBg/50 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                  style={{ border: "1px solid transparent" }}
                >
                  <div className="flex items-center gap-x-2.5 min-h-[28px]">
                    <Settings2
                      strokeWidth={1.75}
                      className="h-[18px] w-[18px] shrink-0 text-mutedText group-hover:text-mainText transition-colors duration-150"
                    />
                    <span className="text-[13px] font-normal tracking-wide text-mutedText group-hover:text-mainText transition-colors duration-150">
                      Manage Rx targets
                    </span>
                  </div>
                </button>
              </div>
            </>
          ) : (
            /* ══ MANAGE MODE ══ */
            <>
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-mainDivider">
                <button
                  type="button"
                  onClick={() => {
                    setManaging(false);
                    setConfirmDelete(null);
                    setConfirmPublic(null);
                  }}
                  className="flex items-center gap-x-1.5 text-[12px] text-mutedText hover:text-mainText transition-colors duration-150 outline-none"
                >
                  <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                  Back
                </button>
                <span className="text-[11px] tabular-nums">
                  <span
                    className={
                      activeAtMax ? "text-amber-400" : "text-activeCase"
                    }
                  >
                    {activeCases.length}
                  </span>
                  <span className="text-mutedText">/{MAX_ACTIVE} active</span>
                </span>
              </div>

              <div className="py-1.5 px-1.5 flex flex-col gap-y-0.5 max-h-[280px] overflow-y-auto">
                {activeCases.map((c) => (
                  <ManageRow key={c.case} company={c} />
                ))}

                {inactiveCases.length > 0 && (
                  <div className={activeCases.length > 0 ? "mt-1" : ""}>
                    <button
                      type="button"
                      onClick={() => {
                        setInactiveExpanded((v) => {
                          if (v) setVisibleInactive(PAGE_SIZE);
                          return !v;
                        });
                      }}
                      className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-mutedText hover:text-mainText hover:bg-mainButtonBg/50 transition-colors duration-150 outline-none"
                    >
                      <span className="text-[11px] uppercase tracking-widest font-medium text-mutedText/50">
                        Inactive
                      </span>
                      <div className="flex items-center gap-x-1.5">
                        <span className="text-[11px] tabular-nums text-mutedText">
                          {inactiveCases.length}
                          <span className="text-mutedText/40">
                            /{MAX_INACTIVE}
                          </span>
                        </span>
                        {inactiveExpanded ? (
                          <ChevronDown
                            className="h-3 w-3 text-mutedText rotate-180 transition-transform duration-200"
                            strokeWidth={2}
                          />
                        ) : (
                          <ChevronDown
                            className="h-3 w-3 text-mutedText transition-transform duration-200"
                            strokeWidth={2}
                          />
                        )}
                      </div>
                    </button>

                    <div
                      className={[
                        "overflow-hidden transition-all duration-200 ease-in-out",
                        inactiveExpanded
                          ? "max-h-[9999px] opacity-100"
                          : "max-h-0 opacity-0",
                      ].join(" ")}
                    >
                      <div className="flex flex-col gap-y-0.5">
                        {inactiveCases.slice(0, visibleInactive).map((c) => (
                          <ManageRow key={c.case} company={c} />
                        ))}
                        {visibleInactive < inactiveCases.length && (
                          <button
                            type="button"
                            onClick={() =>
                              setVisibleInactive((v) => v + PAGE_SIZE)
                            }
                            className="text-[11px] text-mutedText hover:text-velBlue transition-colors py-1.5 rounded-lg hover:bg-mainButtonBg/30"
                          >
                            Load{" "}
                            {Math.min(
                              PAGE_SIZE,
                              inactiveCases.length - visibleInactive,
                            )}{" "}
                            more
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {companies.length === 0 && (
                  <p className="text-[12px] text-mutedText text-center py-4">
                    No Rx brand names yet.
                  </p>
                )}
              </div>

              <div className="border-t border-mainDivider mx-1.5" />
              <div className="px-1.5 py-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setManaging(false);
                    setShowAddCompany(true);
                  }}
                  disabled={companies.length >= MAX_ACTIVE + MAX_INACTIVE}
                  className={[
                    "group w-full flex items-center gap-x-2.5 rounded-lg px-2.5 py-1.5 transition-colors duration-150 outline-none",
                    companies.length >= MAX_ACTIVE + MAX_INACTIVE
                      ? "text-mutedText/40 cursor-not-allowed"
                      : "text-mutedText hover:text-mainText hover:bg-mainButtonBg/50",
                  ].join(" ")}
                  style={{ border: "1px solid transparent" }}
                >
                  <Plus
                    className="h-[18px] w-[18px] shrink-0"
                    strokeWidth={2}
                  />
                  <span className="text-[13px] font-normal tracking-wide">
                    Add Rx target
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CompanySelect;
